'use client';

import { useEffect, useRef } from 'react';

import { useInView, useReducedMotion } from 'motion/react';

interface CountingNumberProps {
  value: number;
  from?: number;
  className?: string;
  prefix?: string;
  suffix?: string;
  decimalPlaces?: number;
}

/** Low, so the count has visible range left to travel under a front-loaded ease. */
const START_RATIO = 0.25;
const DURATION_MS = 1100;
function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function format({ value, decimalPlaces }: { value: number; decimalPlaces: number }) {
  return decimalPlaces > 0 ? value.toFixed(decimalPlaces) : Math.floor(value).toString();
}

/**
 * A figure that counts up as it arrives.
 *
 * Writes straight to the text node: seven of these cross the platform band together, and a
 * `setState` per frame per tile is seven render-and-commit passes a frame for a second.
 *
 * The server renders the real figure, not the ramp-in — these are published statistics and the
 * initial HTML is what a crawler and a no-JS visitor read.
 */
export function CountingNumber({
  value,
  from,
  className,
  prefix = '',
  suffix = '',
  decimalPlaces = 0,
}: CountingNumberProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const valueRef = useRef<HTMLSpanElement>(null);
  // Armed before the tile arrives, rather than once it is well inside: the drop to the ramp-in
  // lands off-screen, and a figure scrolled past quickly still settles on the truth.
  const isInView = useInView(ref, { once: true, margin: '120px' });
  const reducedMotion = useReducedMotion();
  const startValue = from ?? value * START_RATIO;

  useEffect(() => {
    const node = valueRef.current;
    if (!node) return;
    if (reducedMotion || !isInView) {
      // React will not repaint this node — the count writes to it directly — so switching reduced
      // motion on mid-ramp would otherwise leave a published figure frozen part-way.
      node.textContent = format({ value, decimalPlaces });
      return;
    }

    const startTime = performance.now();
    let frame = 0;

    function step(now: number) {
      const progress = Math.min((now - startTime) / DURATION_MS, 1);
      node!.textContent = format({
        value: startValue + (value - startValue) * easeOutCubic(progress),
        decimalPlaces,
      });
      if (progress < 1) frame = requestAnimationFrame(step);
    }

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [isInView, value, startValue, reducedMotion, decimalPlaces]);

  // Holds the widest the figure will ever be, so counting cannot reflow the row around it.
  const start = format({ value: startValue, decimalPlaces });
  const end = format({ value, decimalPlaces });
  const spacer = start.length >= end.length ? start : end;

  return (
    <span ref={ref} className={`inline-grid tabular-nums ${className ?? ''}`}>
      <span className="invisible col-start-1 row-start-1" aria-hidden="true">
        {prefix}
        {spacer}
        {suffix}
      </span>
      <span className="col-start-1 row-start-1">
        {prefix}
        <span ref={valueRef}>{end}</span>
        {suffix}
      </span>
    </span>
  );
}
